import React, { useMemo } from 'react';
import { TeamInfo } from '../TeamInfo';
import { LapHistory, stintLaps } from '../analytics/lapHistory';
import { paceOf } from '../analytics/strategy';
import { LapSeries, LapTraceChart } from './charts/LapTraceChart';
import { PaceDistribution, PaceEntry } from './charts/PaceDistribution';
import './ChartsPanel.css';

interface ChartsPanelProps {
  teams: Map<string, TeamInfo>;
  lapHistory: LapHistory;
  /** Highlighted teams; these are the ones traced. */
  ourTeamIds: Set<string>;
}

/** Traces get unreadable past a handful of overlaid lines. */
const MAX_TRACED = 6;

export function labelOf(team: TeamInfo): string {
  const name = team.teamName?.trim() ?? '';
  const driver = team.driver?.trim() ?? '';
  if (name && driver && name.toLowerCase() !== driver.toLowerCase()) {
    return `${name} (${driver})`;
  }
  return name || driver || `Kart ${team.kartNumber || '—'}`;
}

export const ChartsPanel: React.FC<ChartsPanelProps> = ({
  teams,
  lapHistory,
  ourTeamIds,
}) => {
  /**
   * Trace the karts marked as ours. With none marked, fall back to the current
   * top few — something useful on screen beats an empty frame and a hint.
   */
  const traced = useMemo(() => {
    const picked = ourTeamIds.size
      ? Array.from(teams.values()).filter((t) => ourTeamIds.has(t.id))
      : Array.from(teams.values())
          .filter((t) => Number.isFinite(parseInt(t.position, 10)))
          .sort((a, b) => parseInt(a.position, 10) - parseInt(b.position, 10));

    return picked.slice(0, MAX_TRACED);
  }, [teams, ourTeamIds]);

  const series = useMemo<LapSeries[]>(
    () =>
      traced.map((team) => ({
        teamId: team.id,
        label: labelOf(team),
        kartNumber: team.kartNumber,
        stints: stintLaps(lapHistory, team.id, team.stintAverages ?? []),
      })),
    [traced, lapHistory],
  );

  const paceEntries = useMemo<PaceEntry[]>(() => {
    const entries: PaceEntry[] = [];
    teams.forEach((team) => {
      const paceSeconds = paceOf(team);
      if (paceSeconds === null) return;
      entries.push({
        teamId: team.id,
        kartNumber: team.kartNumber,
        label: labelOf(team),
        paceSeconds,
        category: team.category,
      });
    });
    return entries;
  }, [teams]);

  return (
    <div className="cp-panel">
      <section className="cp-section">
        <div className="cp-head">
          <h3>Lap trace</h3>
          <span className="cp-note">
            {ourTeamIds.size
              ? `${traced.length} marked kart${traced.length === 1 ? '' : 's'}`
              : 'Top of the field — click grid rows to trace your own'}
          </span>
        </div>
        <LapTraceChart series={series} />
      </section>

      <section className="cp-section">
        <div className="cp-head">
          <h3>Field pace</h3>
          <span className="cp-note">
            Clean average per kart · {paceEntries.length} running
          </span>
        </div>
        <PaceDistribution entries={paceEntries} ourTeamIds={ourTeamIds} />
      </section>
    </div>
  );
};

export default ChartsPanel;
